/**
 * 由標籤統計建出媒體庫的虛擬資料夾樹。
 *
 * 「資料夾」不是真的目錄——媒體庫的物件 key 是 sha256，儲存層完全扁平。樹是由
 * 標籤（`0/zh/4_food`）在 CMS 端切出來的視圖，資料庫那邊只是 `tags text[]`。
 *
 * 判斷一個標籤是不是資料夾，**不能只看有沒有 `/`**：`0` 沒有斜線，卻是整棵
 * ITRI 素材樹的根（`0/zh`、`0/en` 都是它的子孫）。正確的判準是「在 trie 裡有
 * 沒有子節點」，故先把所有標籤切段建 trie，再看結果。沒有子節點又不含 `/` 的
 * （`mascot`、`ip`）才是平面標記，另外列，不假裝它們是資料夾。
 *
 * 與 React 無關的純函式，故放在 lib 而非元件檔——{@link ../components/MediaFolderTree}
 * 只負責畫，這裡負責算，後者才測得動。
 */

import type { TagFacet } from './api-client';

export interface FolderNode {
  /** 完整路徑，即標籤原值。 */
  path: string;
  /** 路徑最後一段，顯示用。 */
  name: string;
  /** 掛在這個標籤本身的素材數。 */
  own: number;
  /** own + 所有子孫的 own。 */
  total: number;
  children: FolderNode[];
}

export interface MediaTree {
  folders: FolderNode[];
  labels: TagFacet[];
}

/**
 * 建樹。
 *
 * 中間層可能沒有任何素材直接掛在上面（例如只有 `0/zh/4_food` 有檔案，`0/zh`
 * 本身沒有），這種節點仍須存在，否則樹會斷成好幾截。故一律先補齊路徑上的每
 * 一段，`own` 留 0。
 */
export function buildMediaTree(facets: TagFacet[]): MediaTree {
  const nodes = new Map<string, FolderNode>();
  const childrenOf = new Map<string, Set<string>>();

  const ensure = (path: string): FolderNode => {
    let n = nodes.get(path);
    if (!n) {
      n = { path, name: path.split('/').pop() ?? path, own: 0, total: 0, children: [] };
      nodes.set(path, n);
      childrenOf.set(path, new Set());
    }
    return n;
  };

  for (const f of facets) {
    const segments = f.tag.split('/');
    for (let i = 0; i < segments.length; i += 1) {
      const path = segments.slice(0, i + 1).join('/');
      ensure(path);
      if (i > 0) childrenOf.get(segments.slice(0, i).join('/'))!.add(path);
    }
    ensure(f.tag).own = f.count;
  }

  const roots: string[] = [];
  for (const path of nodes.keys()) {
    if (!path.includes('/')) roots.push(path);
  }

  // 無子節點且無斜線 → 平面標記，不進樹
  const labels: TagFacet[] = [];
  const folderRoots: string[] = [];
  for (const path of roots) {
    if (childrenOf.get(path)!.size === 0) {
      labels.push({ tag: path, count: nodes.get(path)!.own });
    } else {
      folderRoots.push(path);
    }
  }

  const assemble = (path: string): FolderNode => {
    const node = nodes.get(path)!;
    node.children = [...childrenOf.get(path)!].sort().map(assemble);
    node.total = node.own + node.children.reduce((s, c) => s + c.total, 0);
    return node;
  };

  return {
    folders: folderRoots.sort().map(assemble),
    labels: labels.sort((a, b) => a.tag.localeCompare(b.tag)),
  };
}
